// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// One-shots, rendered once — most of them when the machine loads (M3-21, 07 §2 and §9).
//
// "Synthesis happens at load, playback happens at runtime." At runtime a coil firing costs a
// buffer source and a gain — not an oscillator, three envelopes and a biquad, forty times a
// second during multiball. The budget is ≤ 220 buffers in ≤ 40 ms on the low tier, and the
// eight coil cues here come to sixty-four of them.
//
// **"At load" turned out to be two different moments.** The bank reached 195 buffers and 54.8 ms
// of Chrome, and nothing left at the top of the profile was a mistake — it was filter passes
// over long buffers, and the shortest way to make them cheaper was to make them sound worse. But
// a load gate is a question about *waiting*, and a cue the player cannot reach in the first
// second is not something they are waiting for. So each cue declares a `CueTiming`: the ones the
// machine is certain to make immediately are rendered before the first frame, and the ones that
// wait on an award, a target, a drain or a shove are rendered a frame at a time afterwards by
// `SoundBank.warmOne`, with `pick` covering anything wanted sooner. See `CueTiming` below.
//
// **Baked variants are the point, not an optimisation.** The same buffer replayed is a machine
// gun: a ball rattling between two bumpers fires the identical waveform four times in three
// hundred milliseconds, and the ear hears a loop rather than a machine. Eight variants at ±4%
// pitch (07 §3.3) is enough that the repeat never lands, and it costs eight small buffers.
//
// **But not every family wants that.** A chime has a *pitch*, and it is the point: varying it
// ±4% puts the machine out of tune with itself, which is a worse artefact than the one the
// variants exist to prevent. A drain happens once a ball and lasts a third of a second, so
// eight of it would be two and a half seconds of render against a repeat the player will hear
// perhaps three times in a multiball. So a cue family declares its own policy rather than the
// bank imposing one: eight for a thump, three for a drain, one for a note.
//
// Which variant plays is chosen from the **audio** generator, never the simulation's. 07 rule 4:
// a replay's sound may vary while its ball may not.

import { Rng } from '@core/rng/Rng'
import type { NoiseSource, Samples } from './dsp'
import { barFor, CHIME_NAMES, renderChime, type ChimeBar } from './chimes'
import { IMPACT_CUES, IMPACT_RECIPES, renderImpact, type ImpactCue } from './impacts'
import { renderDrainRoll } from './rolling'
import { renderSpinnerClick } from './spinner'
import { TONE_CUES, TONE_RECIPES, type ToneCue } from './tones'
import {
  renderCaptiveClick,
  renderDropTargetClack,
  renderGateRattle,
  renderStandupTick,
} from './targets'
import {
  renderBobTing,
  renderCabinetShove,
  renderPlungerRelease,
  renderTiltBuzz,
  renderTiltWarning,
} from './cabinet'
import {
  COIL_VOICES,
  FLIPPER_RETURN,
  noiseFrom,
  renderSolenoid,
  SOLENOID_RECIPES,
  type CoilVoice,
} from './solenoids'
import type { SynthName } from '@core/machine'
import type { AudioBufferLike, AudioPlaybackContext } from './webAudio'

/**
 * Cue names the bank holds.
 *
 * The eight coils, the flipper's return, the three chime bars, the eight ball impacts, the drain
 * roll, the spinner click, the four mechanisms and the five cabinet cues — thirty-one cues and
 * **195 buffers**, against 07 §9's cap of 220. A hundred and eighteen of them exist before the
 * first frame and the other seventy-seven arrive over the frames after it; the cap counts all
 * of them, because it is a memory budget and deferring a render does not save memory.
 *
 * **That cap is the binding constraint and the variant counts are chosen against it.** The
 * mechanical layer is complete at 195; what is still to come is speech (M3-27, roughly one
 * buffer per callout) and the rule-driven game tones (M3-29, seven synths). Eight variants each
 * for the cabinet cues would have spent the remaining twenty-five on sounds a player hears twice
 * a ball, which is why a plunge gets three and a shove gets four while a ball impact still gets
 * eight. Music (M3-28) is sequenced rather than baked and costs none of this.
 *
 * The names are the machine format's own wherever the format has one. `SYNTH_NAMES` in
 * `core/ts/machine/types.ts` is the vocabulary a definition may name a cue from, and its comment
 * says what happens when the two lists drift: "a cue naming a synth outside it has no recipe and
 * would play silence, which is the worst possible failure mode because the machine still loads
 * and still scores". `SYNTH_CUES` below is the join between them, and a test walks it.
 */
export type CueName =
  | CoilVoice
  | 'flipperReturn'
  | ChimeBar
  | ImpactCue
  | 'drainRoll'
  | 'spinnerClick'
  | MechanismCue
  | CabinetCue
  | ToneCue

/** The mechanisms of 07 §3.5, named as the machine format's `SYNTH_NAMES` names them. */
export const MECHANISM_CUES = [
  'dropTargetClack',
  'standupTick',
  'captiveClick',
  'gateRattle',
] as const
export type MechanismCue = (typeof MECHANISM_CUES)[number]

/** The plunger, the cabinet and the tilt (07 §3.6), named as `SYNTH_NAMES` names them. */
export const CABINET_CUES = [
  'plungerRelease',
  'cabinetShove',
  'tiltBob',
  'tiltWarning',
  'tiltBuzz',
] as const
export type CabinetCue = (typeof CABINET_CUES)[number]

export const CUE_NAMES: readonly CueName[] = [
  ...COIL_VOICES,
  'flipperReturn',
  ...CHIME_NAMES,
  ...IMPACT_CUES,
  'drainRoll',
  'spinnerClick',
  ...MECHANISM_CUES,
  ...CABINET_CUES,
  ...TONE_CUES,
]

/** Baked variants for a broadband one-shot (07 §3.3). */
export const VARIANTS_PER_CUE = 8

/** Pitch spread across the variants, either side of nominal. */
export const PITCH_VARIANCE = 0.04

/**
 * Variants for a pitched cue: **one**.
 *
 * Measured, not assumed. Two variants of an 880 Hz bar that differed only in the strike came
 * out 88 samples apart out of 29,000 — the hammer is a millisecond and the ring is half a
 * second — so the second buffer was a duplicate of the first with a different fingerprint. And
 * the physical answer agrees: a bar struck twice by the same hammer sounds the same, and a
 * machine whose chimes varied would sound broken rather than alive.
 */
export const VARIANTS_PER_PITCHED_CUE = 1

/**
 * Variants for a long cue: **three**.
 *
 * The drain roll is the only cue in the product longer than a quarter of a second, and it plays
 * once per ball — except in multiball, where three can drain inside a few seconds, which is
 * exactly when hearing the identical buffer three times would be noticeable. Three covers that
 * case and costs a second of render; eight would cost two and a half for nothing.
 */
export const VARIANTS_PER_LONG_CUE = 3

/**
 * Variants for a cue a player causes deliberately: **four**.
 *
 * A nudge and a tilt-bob contact are not machine-gun cases — nobody shoves a cabinet forty times
 * a second — but they do come in bursts of two or three while a player is fighting a ball, so one
 * buffer would be heard as a loop. Four covers a burst and costs half of eight, and the budget
 * has started to matter: see the note on `CUE_NAMES`.
 */
export const VARIANTS_PER_OCCASIONAL_CUE = 4

/**
 * When a cue is baked (M3-21, 07 §9).
 *
 * The budget 07 §9 sets is "≤ 220 buffers in ≤ 40 ms on the low tier", and the mechanical layer
 * arrived at 195 buffers in 54.8 ms of Chrome. Every remaining cost is a filter pass over a long
 * buffer, so there is no optimisation left that does not also make something sound worse — but
 * the gate is not really about *how much* is rendered. It is about how long a player waits
 * between asking for a machine and being able to play it.
 *
 * **So the answer is a change of `when`.** A cue the machine is certain to make in the opening
 * second — a flipper, a bumper, a ball hitting a rubber, the plunger the player is already
 * pulling — must exist before the first frame. A cue that waits on something the player has not
 * done yet does not: a knocker may never sound at all, a tilt buzzer sounds at most once a ball,
 * and a drain roll is a whole ball's play away. Those are rendered `'firstUse'`, which takes
 * them out of the load gate entirely.
 *
 * Deferred is not the same as lazy-and-forgotten. `SoundBank.warmOne` renders one pending cue
 * per frame from the moment the machine is up, so all of them are in memory within a fifth of a
 * second and the first-use path is a fallback for anything wanted sooner than that. What the
 * player gets is a machine that starts faster and never hitches; what the cap gets is nothing,
 * because every buffer still exists a moment later. **The 220 cap is a memory budget and this
 * does not move it** — only the 40 ms one.
 */
export type CueTiming = 'load' | 'firstUse'

/**
 * How a family of cues is baked.
 *
 * `spread` is what `render` receives as `scale`, and what it *means* is the family's business:
 * a solenoid reads it as pitch, because a thump has no tuning to lose. A family that has
 * nothing to vary sets one variant and a spread of zero, and the loop below does the right
 * thing without a special case.
 *
 * `timing` has no default on purpose. A new cue has to say whether the machine can be played
 * before it exists, and the honest answer is not guessable from the recipe.
 */
export interface CueSpec {
  readonly variants: number
  readonly spread: number
  readonly timing: CueTiming
  readonly render: (sampleRate: number, noise: NoiseSource, scale: number) => Samples
}

/**
 * Cues that wait on something the player has not done yet.
 *
 * Chosen by *certainty*, not by cost. A cue is here when a machine can be played for a second
 * without it, and the reasons differ:
 *
 * - `kickback`, `scoopEject`, `knocker`, `bankReset` — coils that fire on an award or a
 *   completed bank. A knocker may not sound in a whole game.
 * - `dropTargetClack`, `standupTick`, `captiveClick`, `gateRattle` — a target or a gate has to
 *   be reached first, and a machine may have none of them.
 * - `drainRoll` — the end of a ball, which is at minimum several seconds away and at that point
 *   the ball is already dead.
 * - `cabinetShove`, `tiltBob`, `tiltWarning`, `tiltBuzz` — the player has to hit the machine.
 *
 * Everything else loads: the flippers and their return, the bumper and the slingshot, the trough
 * kick that serves the ball, all eight ball impacts, the chime bars, the spinner, and the
 * plunger release the player fires within the first second of the ball.
 */
const FIRST_USE: ReadonlySet<string> = new Set<CueName>([
  'kickback',
  'scoopEject',
  'knocker',
  'bankReset',
  'dropTargetClack',
  'standupTick',
  'captiveClick',
  'gateRattle',
  'drainRoll',
  'cabinetShove',
  'tiltBob',
  'tiltWarning',
  'tiltBuzz',
  // The game tones (M3-29). Every one of them waits on a rule firing, and half of them wait on
  // a mode the player has to reach first.
  ...TONE_CUES,
])

function timingOf(cue: CueName): CueTiming {
  return FIRST_USE.has(cue) ? 'firstUse' : 'load'
}

/**
 * Cues that never repeat inside a second, and so do not need eight of anything.
 *
 * **The variant policy was applied by family and never by frequency, and that was the mistake.**
 * Eight variants exist to stop a *rattle* sounding like a machine gun — a ball bouncing between
 * two bumpers fires the identical waveform four times in three hundred milliseconds. A knocker
 * fires when a replay is awarded, which is a few times a game and seconds apart; eight of it is
 * seven buffers a player will never hear twice close enough together to compare.
 *
 * It stopped being a matter of taste when the cap became per-machine. The bank plus NEON NOVA's
 * ten callouts, the score's seven notes and the three continuous beds came to **222 against 07
 * §9's 220**, and the honest place to find the difference was here rather than in the cap.
 * Cutting these six to four takes it to 198 and leaves room for a machine with twice as much to
 * say — which is the case the cap now has to survive.
 *
 * Everything that *can* rattle keeps its eight: the flippers, the bumper, the slingshot, the
 * trough kick (three balls served in a second at the start of multiball), the spinner, every
 * ball impact, and the drop targets and standups a player can rake across.
 */
const RARE_CUES: ReadonlySet<string> = new Set<CueName>([
  'knocker',
  'scoopEject',
  'kickback',
  'bankReset',
  'captiveClick',
  'gateRattle',
])

function variantsFor(cue: CueName): number {
  return RARE_CUES.has(cue) ? VARIANTS_PER_OCCASIONAL_CUE : VARIANTS_PER_CUE
}

/** Every cue the bank knows how to make, and how each wants to be baked. */
export const CUE_SPECS: Readonly<Record<CueName, CueSpec>> = buildSpecs()

function buildSpecs(): Record<CueName, CueSpec> {
  const specs = {} as Record<CueName, CueSpec>
  for (const voice of COIL_VOICES) {
    const recipe = SOLENOID_RECIPES[voice]
    specs[voice] = {
      variants: variantsFor(voice),
      spread: PITCH_VARIANCE,
      timing: timingOf(voice),
      render: (rate, noise, scale) => renderSolenoid(recipe, rate, noise, scale),
    }
  }
  specs.flipperReturn = {
    variants: VARIANTS_PER_CUE,
    spread: PITCH_VARIANCE,
    timing: timingOf('flipperReturn'),
    render: (rate, noise, scale) => renderSolenoid(FLIPPER_RETURN, rate, noise, scale),
  }
  for (const name of CHIME_NAMES) {
    const hz = barFor(name)
    specs[name] = {
      variants: VARIANTS_PER_PITCHED_CUE,
      spread: 0,
      timing: timingOf(name),
      render: (rate, noise) => renderChime(hz, rate, noise),
    }
  }
  // Impacts take the full pitch spread. A ball rattling in a lane is the machine-gun case the
  // variants exist for, and an impact has no tuning a spread could damage.
  for (const cue of IMPACT_CUES) {
    const recipe = IMPACT_RECIPES[cue]
    specs[cue] = {
      variants: VARIANTS_PER_CUE,
      spread: PITCH_VARIANCE,
      timing: timingOf(cue),
      render: (rate, noise, scale) => renderImpact(recipe, rate, noise, scale),
    }
  }
  specs.drainRoll = {
    variants: VARIANTS_PER_LONG_CUE,
    spread: PITCH_VARIANCE,
    timing: timingOf('drainRoll'),
    render: (rate, noise, scale) => renderDrainRoll(rate, noise, scale),
  }
  // The spinner is the machine-gun case in its purest form — forty of the same cue a second —
  // so it takes the full eight and costs a tenth of a second of render for all of them.
  specs.spinnerClick = {
    variants: VARIANTS_PER_CUE,
    spread: PITCH_VARIANCE,
    timing: timingOf('spinnerClick'),
    render: (rate, noise, scale) => renderSpinnerClick(rate, noise, scale),
  }
  const mechanisms: Record<MechanismCue, CueSpec['render']> = {
    dropTargetClack: (rate, noise, scale) => renderDropTargetClack(rate, noise, scale),
    standupTick: (rate, noise, scale) => renderStandupTick(rate, noise, scale),
    captiveClick: (rate, noise, scale) => renderCaptiveClick(rate, noise, scale),
    gateRattle: (rate, noise, scale) => renderGateRattle(rate, noise, scale),
  }
  for (const cue of MECHANISM_CUES) {
    specs[cue] = {
      variants: variantsFor(cue),
      spread: PITCH_VARIANCE,
      timing: timingOf(cue),
      render: mechanisms[cue],
    }
  }

  // A plunge happens once a ball, so three is generous; a shove and a bob contact come in
  // bursts of two or three, so they get four.
  specs.plungerRelease = {
    variants: VARIANTS_PER_LONG_CUE,
    spread: PITCH_VARIANCE,
    timing: timingOf('plungerRelease'),
    render: (rate, noise, scale) => renderPlungerRelease(rate, noise, scale),
  }
  specs.cabinetShove = {
    variants: VARIANTS_PER_OCCASIONAL_CUE,
    spread: PITCH_VARIANCE,
    timing: timingOf('cabinetShove'),
    render: (rate, noise, scale) => renderCabinetShove(rate, noise, scale),
  }
  specs.tiltBob = {
    variants: VARIANTS_PER_OCCASIONAL_CUE,
    spread: PITCH_VARIANCE,
    timing: timingOf('tiltBob'),
    render: (rate, noise, scale) => renderBobTing(rate, noise, scale),
  }
  // **One each, and varying them would be a defect.** A buzzer is an electromechanical device
  // hammering at a fixed rate; two that differed would sound like two buzzers. This is the same
  // argument as the chime bars', arriving from the opposite direction: there, varying the pitch
  // would put the machine out of tune with itself; here, varying anything at all would make a
  // fault sound intermittent.
  specs.tiltWarning = {
    variants: 1,
    spread: 0,
    timing: timingOf('tiltWarning'),
    render: (rate) => renderTiltWarning(rate),
  }
  specs.tiltBuzz = {
    variants: 1,
    spread: 0,
    timing: timingOf('tiltBuzz'),
    render: (rate, noise) => renderTiltBuzz(rate, noise),
  }

  // The game tones: **one variant each, and not because of the budget.** These are pitched and
  // they carry meaning — a jackpot four per cent sharp of the last jackpot sounds like a
  // different award. The variance exists to stop a *rattle* sounding like a machine gun, and
  // none of these ever repeats fast enough to rattle.
  for (const cue of TONE_CUES) {
    const render = TONE_RECIPES[cue]
    specs[cue] = {
      variants: VARIANTS_PER_PITCHED_CUE,
      spread: 0,
      timing: timingOf(cue),
      render: (rate, noise) => render(rate, noise),
    }
  }
  return specs
}

/**
 * The join between the machine format's synthesis vocabulary and this bank.
 *
 * `SYNTH_NAMES` (04, `core/ts/machine/types.ts`) is what a machine definition may name a cue
 * from, and its own comment states the failure it is guarding against: "a cue naming a synth
 * outside it has no recipe and would play silence, which is the worst possible failure mode
 * because the machine still loads and still scores". Nothing checked it. Two vocabularies grew
 * up beside each other — the format's authoring names and this bank's recipe names — with no
 * relation between them and nothing to notice when they drifted.
 *
 * They are not the same list and should not be. A format name is what an *author* asks for, and
 * one of them can map to several recipes: `chime` is three bars, `ballImpact` is eight surfaces.
 * Others go the other way — `flipper` is played automatically by the machine and no definition
 * ever names it, so it is in the bank and not in the vocabulary. What has to be true is that
 * every name an author can write resolves to something that makes a sound, and that is what this
 * table says and `soundBank.test.ts` walks.
 *
 * An empty array is an honest "not yet", carrying the milestone that will fill it. It is not the
 * same as a name being absent: absent means nobody has noticed.
 */
export const SYNTH_CUES: Readonly<Record<SynthName, readonly CueName[]>> = {
  // 07 §3.1 — solenoids.
  solenoidThunk: ['kickback'],
  popBumper: ['popBumper'],
  slingshot: ['slingshot'],
  scoopEject: ['scoopEject'],
  kickback: ['kickback'],
  knocker: ['knocker'],
  troughKick: ['troughKick'],
  // 07 §3.5 — targets and mechanisms.
  dropTargetClack: ['dropTargetClack'],
  bankReset: ['bankReset'],
  standupTick: ['standupTick'],
  captiveClick: ['captiveClick'],
  spinnerClick: ['spinnerClick'],
  gateRattle: ['gateRattle'],
  /** No diverter exists in the simulation yet, so nothing can name one and mean it. */
  diverter: [],
  // 07 §3.6 — the plunger and the cabinet. M3-26's second half.
  /** A continuous scrape rather than a one-shot, so it has no buffer — like `ballRoll`. */
  plungerPull: [],
  plungerRelease: ['plungerRelease'],
  cabinetShove: ['cabinetShove'],
  tiltBob: ['tiltBob'],
  tiltWarning: ['tiltWarning'],
  tiltBuzz: ['tiltBuzz'],
  // 07 §3.3 — the ball. `ballRoll` is a continuous bed rather than a cue, so it has no buffer
  // in the bank at all; the entry records that it is implemented, not that it is a one-shot.
  ballRoll: [],
  ballImpact: [
    'impactRubber',
    'impactFlipperRubber',
    'impactMetal',
    'impactWood',
    'impactPlastic',
    'impactWire',
    'impactGlass',
  ],
  ballBall: ['impactBall'],
  drain: ['drainRoll'],
  // 07 §3.2 — game tones. The chime bars, and the seven rule-driven tones of M3-29.
  chime: [...CHIME_NAMES],
  brassStab: ['brassStab'],
  sweepUp: ['sweepUp'],
  sweepDown: ['sweepDown'],
  blip: ['blip'],
  arpRun: ['arpRun'],
  noiseBurst: ['noiseBurst'],
  subDrop: ['subDrop'],
}

/**
 * Vocabulary names with no recipe yet, and the milestone that gives them one.
 *
 * Listed rather than inferred from an empty array, so that adding a name to the format without
 * a recipe fails the test instead of quietly joining this list.
 */
export const SYNTH_PENDING: Readonly<Record<string, string>> = {
  diverter: 'no diverter mechanism exists in the simulation',
  plungerPull: 'M3-26 — a continuous scrape, so it has no one-shot buffer',
  ballRoll: 'M3-24 — a continuous bed, so it has no one-shot buffer',
}

/**
 * The cues a machine declares that would play nothing, with the reason for each.
 *
 * This is what makes `SYNTH_CUES` load-bearing rather than a table somebody keeps up to date out
 * of good intentions. 04 §12 lets a definition name a synth per cue, and the format's own
 * comment says what happens when it names one with no recipe: silence, from a machine that
 * still loads and still scores. So the same thing is done here as `loadMachine` does for device
 * kinds it has no mechanism for — report it, rather than skip it quietly.
 *
 * NEON NOVA declares sixteen cues, and the ones naming the rule-driven game tones of M3-29 come
 * back here until that milestone lands. That is the point: the list should be visibly non-empty
 * while the work is outstanding, and empty when it is done.
 */
export function silentCues(
  cues: Readonly<Record<string, Readonly<Record<string, unknown>>>> | undefined,
): string[] {
  const out: string[] = []
  if (cues === undefined) return out
  for (const id of Object.keys(cues).sort()) {
    const synth = cues[id]?.['synth']
    if (typeof synth !== 'string') {
      out.push(`${id}: no synth named`)
      continue
    }
    const recipes = SYNTH_CUES[synth as SynthName] as readonly CueName[] | undefined
    if (recipes === undefined) {
      out.push(`${id}: ${synth} is not in the format's vocabulary`)
      continue
    }
    if (recipes.length > 0) continue
    out.push(`${id}: ${synth} (${SYNTH_PENDING[synth] ?? 'no recipe and no reason recorded'})`)
  }
  return out
}

/**
 * A seed for the audio streams.
 *
 * Fixed rather than random, so a machine sounds the same on every load and a spectral fixture
 * comparing the web and Unity renders has something to compare. Varying it would make the
 * parity suite of 07 §9 impossible to write.
 */
export const AUDIO_SEED = 0x41554449

/**
 * The noise stream a cue renders from.
 *
 * **One stream per cue, not one for the whole bank.** The bank used to draw every cue from a
 * single generator walked in `CUE_NAMES` order, which made a cue's waveform depend on everything
 * rendered before it: adding a recipe silently changed the sound of every cue after it in the
 * list, and 07 §9's parity fixtures would have to be regenerated for a change that touched
 * nothing they cover.
 *
 * Deferred rendering turns that coupling from a nuisance into a correctness problem. A cue
 * rendered at first use is not preceded by the same draws as one rendered at load, so under a
 * shared stream the *same* cue would sound different depending on when the player happened to
 * shove the cabinet. Seeding from the cue's own name removes the question: a buffer is a
 * function of its name, its variant index and the sample rate, and of nothing else.
 */
export function cueSeed(cue: CueName): number {
  // FNV-1a over the name. Any dispersal would do; this one is three lines and is the same
  // arithmetic in C#, which matters when the Unity core renders the same bank.
  let h = 0x811c9dc5
  for (let i = 0; i < cue.length; i++) {
    h = Math.imul(h ^ cue.charCodeAt(i), 0x01000193)
  }
  return (h ^ AUDIO_SEED) | 0
}

/**
 * Every variant of one cue.
 *
 * The single place a buffer is made, so the load path and the first-use path cannot drift. A
 * test asserts the two produce identical samples, which is the property that makes deferring a
 * cue safe rather than a way of changing how it sounds.
 */
export function renderCue(cue: CueName, sampleRate: number): Samples[] {
  const spec = CUE_SPECS[cue]
  const noise = noiseFrom(new Rng(cueSeed(cue)))
  const variants: Samples[] = []
  for (let i = 0; i < spec.variants; i++) {
    // Spread evenly rather than randomly. Random values cluster, and a cluster is the
    // machine-gun effect returning for two or three of the eight.
    variants.push(spec.render(sampleRate, noise, 1 + spreadFor(i, spec.variants) * spec.spread))
  }
  return variants
}

/** Rendered samples, before they become anything a browser can play. */
export class RenderedCues {
  readonly sampleRate: number
  readonly cues: ReadonlyMap<CueName, readonly Samples[]>
  /** Wall-clock cost of the render, milliseconds. Reported, never asserted in the suite. */
  readonly renderMs: number

  private constructor(sampleRate: number, cues: Map<CueName, Samples[]>, renderMs: number) {
    this.sampleRate = sampleRate
    this.cues = cues
    this.renderMs = renderMs
  }

  /**
   * Render the cues with the given timings.
   *
   * Defaults to `'load'` alone, which is what loading a machine does and what 07 §9's 40 ms gate
   * measures. A caller that wants the whole bank at once — the parity fixtures, and the tests
   * that assert the buffer cap — asks for both.
   *
   * `now` is injected so the cost can be measured without this module reaching for a clock,
   * which also keeps it usable from a test that does not want one.
   */
  static render(
    sampleRate: number,
    now: () => number = () => 0,
    timings: readonly CueTiming[] = ['load'],
  ): RenderedCues {
    const started = now()
    const cues = new Map<CueName, Samples[]>()
    for (const name of CUE_NAMES) {
      if (!timings.includes(CUE_SPECS[name].timing)) continue
      cues.set(name, renderCue(name, sampleRate))
    }
    return new RenderedCues(sampleRate, cues, now() - started)
  }

  get bufferCount(): number {
    let n = 0
    for (const variants of this.cues.values()) n += variants.length
    return n
  }

  variantsOf(cue: CueName): readonly Samples[] {
    return this.cues.get(cue) ?? []
  }
}

/**
 * The rendered cues, uploaded as playable buffers.
 *
 * Split from `RenderedCues` because the samples are testable and the upload is not: everything
 * worth asserting about a coil sound is in the `Float32Array`, and everything a browser needs is
 * on the other side of `createBuffer`.
 *
 * The bank also owns the deferred half of the render (M3-21). A cue whose timing is `'firstUse'`
 * arrives here as an absence, and there are two ways it stops being one: `warmOne`, called once
 * a frame by the host until nothing is pending, and `pick`, which renders on the spot for
 * anything wanted before the warm-up gets to it. The second path is the one that must not be
 * removed as redundant — it is what makes the deferral safe rather than a race between the
 * player and the loader.
 */
export class SoundBank {
  readonly sampleRate: number
  /** Cost of the load render, milliseconds — the number 07 §9's 40 ms gate is about. */
  readonly renderMs: number
  /** Cost of everything rendered after load, milliseconds. Stops growing when `pending` is 0. */
  lateRenderMs = 0
  private readonly context: AudioPlaybackContext
  private readonly buffers: Map<CueName, AudioBufferLike[]>
  private readonly waiting: CueName[]
  private readonly rng: Rng
  private readonly now: () => number

  constructor(
    context: AudioPlaybackContext,
    rendered: RenderedCues,
    seed = AUDIO_SEED ^ 0x2f,
    now: () => number = () => 0,
  ) {
    this.sampleRate = rendered.sampleRate
    this.renderMs = rendered.renderMs
    this.context = context
    this.rng = new Rng(seed)
    this.now = now
    this.buffers = new Map()
    this.waiting = []

    for (const cue of CUE_NAMES) {
      const samples = rendered.variantsOf(cue)
      // Anything the render did not produce is pending, whatever its declared timing. A caller
      // that renders a subset gets a bank that fills itself in rather than one with holes in it.
      if (samples.length === 0) {
        this.waiting.push(cue)
        continue
      }
      this.buffers.set(
        cue,
        samples.map((one) => this.upload(one)),
      )
    }
  }

  /** Build straight from a context, which is what a machine load actually does. */
  static forContext(context: AudioPlaybackContext, now?: () => number): SoundBank {
    return new SoundBank(context, RenderedCues.render(context.sampleRate, now), undefined, now)
  }

  get bufferCount(): number {
    let n = 0
    for (const list of this.buffers.values()) n += list.length
    return n
  }

  /** Cues not rendered yet. Zero once the warm-up has caught up. */
  get pending(): number {
    return this.waiting.length
  }

  /**
   * Render one pending cue, if there is one.
   *
   * Called once a frame by the host. One cue is between a hundredth of a millisecond and a
   * couple of milliseconds out of a sixteen-millisecond frame, and the whole deferred set is in
   * memory inside a fifth of a second — long before a player has plunged, let alone tilted.
   *
   * Returns what it rendered, so a caller can report progress rather than guess at it.
   */
  warmOne(): CueName | undefined {
    const cue = this.waiting[0]
    if (cue === undefined) return undefined
    this.realise(cue)
    return cue
  }

  /**
   * A variant of a cue, chosen from the audio stream. Undefined if the cue is unknown.
   *
   * Renders a pending cue rather than returning nothing for it. A deferred cue that answered
   * `undefined` until the warm-up reached it would be a sound that goes missing on a fast
   * machine and arrives on a slow one, which is a worse defect than the load cost it avoids.
   */
  pick(cue: CueName): AudioBufferLike | undefined {
    let list = this.buffers.get(cue)
    if (list === undefined && this.waiting.includes(cue)) list = this.realise(cue)
    if (list === undefined || list.length === 0) return undefined
    return list[this.rng.nextInt(list.length)]
  }

  variantCount(cue: CueName): number {
    return this.buffers.get(cue)?.length ?? 0
  }

  private realise(cue: CueName): AudioBufferLike[] {
    const at = this.waiting.indexOf(cue)
    if (at >= 0) this.waiting.splice(at, 1)
    const started = this.now()
    const uploaded = renderCue(cue, this.sampleRate).map((samples) => this.upload(samples))
    this.lateRenderMs += this.now() - started
    this.buffers.set(cue, uploaded)
    return uploaded
  }

  private upload(samples: Samples): AudioBufferLike {
    const buffer = this.context.createBuffer(1, samples.length, this.sampleRate)
    buffer.getChannelData(0).set(samples)
    return buffer
  }
}

/** Where variant `i` of `count` sits in the pitch spread, −1 to 1. One variant sits at nominal. */
function spreadFor(i: number, count: number): number {
  return count <= 1 ? 0 : (i / (count - 1)) * 2 - 1
}

